import { Box } from "@mui/material";
import { TabLinks } from "components/TabLinks";
import { useTenant } from "hooks/useTenant";
import { Outlet, useMatch } from "react-router-dom";

import { InvoiceTrackingDateFilter } from "./InvoiceTrackingDateFilter";
import { periodError, periodSearch, useInvoiceTrackingPeriod } from "./invoiceTrackingPeriod";

export function WasteInvoicesLayout() {
  const { tenantId } = useTenant();
  const { period, setPeriod } = useInvoiceTrackingPeriod();

  const base = `/o/${tenantId}/waste/invoices`;
  const auditMatch = useMatch({ path: `${base}/audit`, end: false });
  const scheduleMatch = useMatch({ path: `${base}/schedule-adherence`, end: false });
  // Every match is read on every render. Folding these into one `||` expression
  // short-circuits, which skips the later hooks on the routes the earlier ones
  // match and changes how many this component calls from route to route.
  const invoicesExact = useMatch({ path: base, end: true });
  const auditExact = useMatch({ path: `${base}/audit`, end: true });
  const scheduleExact = useMatch({ path: `${base}/schedule-adherence`, end: true });
  // A period scopes a list; one invoice is not a period. Shown on the three
  // lists and nowhere else, so a detail page cannot leave a reader looking at a
  // range that has nothing to do with what is on screen.
  const onList = invoicesExact != null || auditExact != null || scheduleExact != null;
  // A tab link carries the period and nothing else. The period is what the three
  // tabs share; their filters are their own — `status` means nothing to the
  // audit, and `totalOverages` is a filter the adherence report refuses outright,
  // so carrying a whole search across would hand the next tab an error rather
  // than the reader's place in the data.
  const search = periodSearch(period);
  // A plain chain rather than nested ternaries: the tabs are a list, and a
  // fourth would otherwise nest one deeper again.
  const activeTab = () => {
    if (auditMatch) return `${base}/audit${search}`;
    if (scheduleMatch) return `${base}/schedule-adherence${search}`;
    return `${base}${search}`;
  };
  return (
    <Box>
      <TabLinks
        // Three long labels overflow a phone: without this the last tab is cut
        // off at the edge with nothing to scroll it into view.
        scrollable
        value={activeTab()}
        tabs={[
          { title: "Invoices", to: `${base}${search}` },
          { title: "Audit", to: `${base}/audit${search}` },
          { title: "Schedule Adherence", to: `${base}/schedule-adherence${search}` },
        ]}
        aria-label="invoice tabs"
        role="navigation"
      />
      {onList ? (
        <InvoiceTrackingDateFilter
          from={period.from}
          through={period.through}
          onChange={setPeriod}
          errorMessage={periodError(period)}
        />
      ) : null}
      <Outlet />
    </Box>
  );
}
